import axios from 'axios';
import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { buildAxiosFetch } from '@lifeomic/axios-fetch';
import {
  getEnvironmentConfig,
  isHostedEnvironment,
  getEnvironment,
} from '@/modules';
import { setContext } from '@apollo/client/link/context';
import { getInstance } from '@/auth';
import { reportError } from '@/modules/errorReporting';
import { typeConversions } from '@/graphql/typeConversions';
import { split } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
import { WebSocketLink } from '@apollo/client/link/ws';
import { onError } from '@apollo/client/link/error';
import { SubscriptionClient } from 'subscriptions-transport-ws';

Vue.use(VueApollo);

// Setup Auth0 authentication with all GraphQL requests.
const authLink = setContext(async (_, { headers }) => {
  const token = await getInstance().getTokenSilently();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const uploadLink = createUploadLink({
  uri: getEnvironmentConfig('graphqlUri'),
  fetch: buildAxiosFetch(axios, (config, input, init) => {
    // Convert multi-part/form-data into application/json mutation when mocking.
    // TODO: Find a better way to support upload mocking with Mock Service Worker.
    if (process.env.VUE_APP_MOCK && config.data instanceof FormData) {
      const operations = JSON.parse(config.data.get('operations'));
      config.data = operations;
      config.headers['content-type'] = 'application/json';
    }

    return {
      ...config,
      withCredentials: true,
      onUploadProgress: init.onUploadProgress,
      cancelToken: init.cancelToken,
    };
  }),
});

// Log any GraphQL errors or network error that occurred
const errorLink = onError(
  ({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      const { operationName, variables } = operation;
      graphQLErrors.forEach((error) => {
        // Include the operation name and variables in the error message
        const variablesJson = JSON.stringify(variables, null, 2);
        const errorMsg = `Error in GraphQL operation '${operationName}', with variables ${variablesJson}: ${error.message}`;
        reportError(errorMsg);
      });
    }
    if (networkError) {
      reportError(networkError);
    }
  }
);

let link;

if (getEnvironment() === 'storybook') {
  // Skip subscriptions and auth for Storybook
  link = errorLink.concat(uploadLink);
} else {
  const subscriptionUrl = isHostedEnvironment()
    ? `wss://${window.location.host}/server/subscriptions`
    : getEnvironmentConfig('graphqlSubscriptionsUri');

  const wsLink = new WebSocketLink(
    new SubscriptionClient(subscriptionUrl, {
      reconnect: true,
      connectionParams: async () => {
        const authService = getInstance();
        return authService.isAuthenticated
          ? { authToken: await authService.getTokenSilently() }
          : {};
      },
    })
  );

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  link = errorLink.concat(
    split(
      // split based on operation type
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      authLink.concat(uploadLink)
    )
  );
}

const cache = new InMemoryCache({
  possibleTypes: typeConversions,
  typePolicies: {
    ScreeningOutcome: {
      fields: {
        // Ensures the field always has a value, preventing re-fetching
        // on queries that request this field.
        // TODO: Figure out why this is necessary.
        optimisticId: {
          read(existing) {
            if (existing) return existing;
            return null;
          },
        },
      },
    },
  },
});

const apolloClient = new ApolloClient({
  link,
  cache,
});

const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
});

export { apolloProvider, apolloClient };
